import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui";
import { Send, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function MessageThread({ memberId, height = "h-80" }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const load = () => api.get(`/messages/${memberId}`).then((r) => setMessages(r.data));
  useEffect(() => { if (memberId) load(); /* eslint-disable-next-line */ }, [memberId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post("/messages", { member_id: memberId, body });
      setBody(""); await load();
    } finally { setBusy(false); }
  };

  const mine = (m) => m.sender_role === user?.role;

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2"><Lock className="h-3 w-3" /> End-to-end secure care-team messaging</div>
      <div className={`${height} overflow-y-auto rounded-lg border border-border bg-surface p-3 space-y-2`} data-testid="message-thread">
        {messages.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No messages yet. Start the conversation.</p>}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${mine(m) ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-lg px-3 py-2 ${mine(m) ? "bg-brand-700 text-white" : "bg-card border border-border"}`}>
              <p className={`text-[10px] font-semibold mb-0.5 ${mine(m) ? "text-brand-200" : "text-muted-foreground"}`}>{m.sender_name} · {m.sender_role.replace("_", " ")}</p>
              <p className="text-sm">{m.body}</p>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2 mt-3">
        <input value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type a secure message…" data-testid="message-input"
          className="h-10 flex-1 rounded-md border border-input bg-card px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        <Button onClick={send} disabled={busy} data-testid="message-send"><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
